import {
  BarChart2,
  LayoutDashboard,
  LogOut,
  Plus,
  Settings2,
  ShieldCheck,
  Ticket,
  Users,
} from 'lucide-react';
import { NavLink, Outlet, useMatch } from 'react-router-dom';

import ThemeToggle from './ThemeToggle';
import BrandMark from './BrandMark';
import AgentStatusDropdown from './AgentStatusDropdown';
import { NotificationBell } from './notifications/NotificationBell';
import { NotificationToastStack } from './notifications/NotificationToast';
import ToastContainer from './Toast';
import { useAuth } from '../contexts/AuthContext';
import { useUnopenedTicketCount } from '../hooks/useUnopenedTicketCount';
import { isOtsEmbed } from '../utils/embed';
import {
  canAccessPlatformConfigSection,
  canAccessRoleManagement,
  canAccessStaffManagement,
  canViewReports,
} from '../utils/permissions';

function userInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? 'nav-item active' : 'nav-item';
}

function isTicketDetailPath(id: string | undefined): boolean {
  return Boolean(id && id !== 'new');
}

export default function Layout() {
  const { user, logout } = useAuth();
  const unopenedCount = useUnopenedTicketCount();
  const ticketMatch = useMatch('/tickets/:id');
  const navRail = isTicketDetailPath(ticketMatch?.params.id);
  const embed = isOtsEmbed();
  const ticketsBadge = unopenedCount > 99 ? '99+' : String(unopenedCount);

  const showAdmin =
    user &&
    (canAccessRoleManagement(user) ||
      canAccessStaffManagement(user) ||
      canAccessPlatformConfigSection(user));

  const roleLabel = user?.role.replace(/_/g, ' ') ?? '';

  return (
    <div className={`app-shell${navRail ? ' app-shell--nav-rail' : ''}${embed ? ' app-shell--embed' : ''}`}>
      <aside className={`sidebar${navRail ? ' sidebar--rail' : ''}`}>
        <div className="sidebar-brand">
          {navRail ? <BrandMark variant="icon" /> : <BrandMark />}
        </div>
        <nav>
          <NavLink to="/" end className={navClass}>
            <LayoutDashboard className="nav-icon" size={navRail ? 22 : 17} strokeWidth={1.75} />
            <span className="nav-label">Dashboard</span>
          </NavLink>
          <NavLink
            to="/tickets"
            className={navClass}
            aria-label={
              unopenedCount > 0 ? `Tickets, ${unopenedCount} unopened` : 'Tickets'
            }
          >
            <Ticket className="nav-icon" size={navRail ? 22 : 17} strokeWidth={1.75} />
            <span className="nav-label">Tickets</span>
            {unopenedCount > 0 && (
              <span className="nav-queue-badge" aria-hidden>
                {ticketsBadge}
              </span>
            )}
          </NavLink>
          <NavLink
            to="/tickets/new"
            className={({ isActive }) =>
              `${navClass({ isActive })}${embed ? ' nav-item-create' : ''}`
            }
          >
            <Plus className="nav-icon" size={navRail ? 22 : 17} strokeWidth={1.75} />
            <span className="nav-label">{navRail ? 'New' : 'Create ticket'}</span>
          </NavLink>
          {user && canViewReports(user) && (
            <NavLink to="/reports" className={navClass}>
              <BarChart2 className="nav-icon" size={navRail ? 22 : 17} strokeWidth={1.75} />
              <span className="nav-label">Reports</span>
            </NavLink>
          )}

          {showAdmin && !navRail && <div className="sidebar-section">Admin</div>}
          {user && canAccessRoleManagement(user) && (
            <NavLink to="/roles" className={navClass}>
              <ShieldCheck className="nav-icon" size={navRail ? 22 : 17} strokeWidth={1.75} />
              <span className="nav-label">{navRail ? 'Roles' : 'Role Management'}</span>
            </NavLink>
          )}
          {user && canAccessStaffManagement(user) && (
            <NavLink to="/users" className={navClass}>
              <Users className="nav-icon" size={navRail ? 22 : 17} strokeWidth={1.75} />
              <span className="nav-label">{navRail ? 'Staff' : 'Staff Management'}</span>
            </NavLink>
          )}
          {user && canAccessPlatformConfigSection(user) && (
            <NavLink to="/platform-config" className={navClass}>
              <Settings2 className="nav-icon" size={navRail ? 22 : 17} strokeWidth={1.75} />
              <span className="nav-label">{navRail ? 'Config' : 'Platform Config'}</span>
            </NavLink>
          )}
        </nav>

        {user && !embed && (
          <div className="sidebar-footer">
            <div className="sidebar-user">
              <span className="sidebar-user-avatar" aria-hidden>
                {userInitials(user.firstName, user.lastName)}
              </span>
              {!navRail && (
                <>
                  <div className="sidebar-user-info">
                    <span className="sidebar-user-name">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="sidebar-user-role">{roleLabel}</span>
                  </div>
                  <button
                    type="button"
                    className="sidebar-logout"
                    onClick={logout}
                    title="Sign out"
                    aria-label="Sign out"
                  >
                    <LogOut size={15} strokeWidth={2} />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </aside>
      <div className="main-content">
        {!embed && (
          <header className="topbar">
            <div className="topbar-user">
              {user && (
                <>
                  <span className="topbar-user-name">
                    {user.firstName} {user.lastName}
                  </span>
                  <AgentStatusDropdown />
                  <span className="role-pill">{roleLabel}</span>
                </>
              )}
            </div>
            <div className="topbar-actions">
              <NotificationBell />
              <ThemeToggle />
              <button
                type="button"
                className="icon-btn"
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut size={16} strokeWidth={2} />
              </button>
            </div>
          </header>
        )}
        <main className="page">
          <Outlet />
        </main>
      </div>
      <NotificationToastStack />
      <ToastContainer />
    </div>
  );
}
